import { apiService } from '../ApiService';
import { CAGridRow, CAGroup, CAId, CAItem, InspectionResultOption } from '@/types/model/corrective-action';
import { isValidToStatusId } from '@/services/data/LookupDataService';
import { CorrectiveActionGroupResponse, CorrectiveActionLineItemResponse } from '@/types/response/CorrectiveActionResponses';
import { lookupDataService } from '../data/LookupDataService';
import { inspectionService } from '../InspectionService';
import { InspectorsResponse, SitesResponse } from '@/types/response/lookupResponses';
import { GetLineItemsResponse } from '@/types/response/InspectionPlanningResponses';

export class CAOrchestrationService {
  private rawData: CorrectiveActionGroupResponse[] | null = null;
  private lineItemsData: GetLineItemsResponse | null = null;

  private async ensureLineItemsLoaded(): Promise<GetLineItemsResponse> {
    if (!this.lineItemsData) {
      this.lineItemsData = await apiService.getContractLineItems();
    }
    return this.lineItemsData;
  }

  /** Fetches and caches the full corrective-actions response; returns top-level groups only. */
  async getGroupedCAData(): Promise<CAGroup[]> {
    this.rawData = await apiService.getContractCorrectiveActions();
    return this.rawData
      .filter(group =>
        group.LineItems.some(li =>
          li.InspectionResults.some(ir => (ir.CorrectiveActions ?? []).length > 0)
        )
      )
      .map(group => ({
        id: group.Id,
        // Some CA group responses include an Identifier numbering field; fall back to Id.
        groupIdentifier: (group as any).Identifier ?? group.Id,
        title: group.Title,
        lineItems: group.LineItems.map(li => ({
          id: li.Id,
          lineItem: li.LineItem,
          title: li.Title,
          groupId: group.Id,
          subItems: (li.InspectionResults ?? []).flatMap(ir => (ir.CorrectiveActions ?? []).map(ca => ({ id: ca.Id, statusId: ca.StatusId, validationStatusId: isValidToStatusId(ca.IsValid) }))),

          correctiveActions: null,
        })).sort((a, b) => a.lineItem.localeCompare(b.lineItem)),
      }))
      .sort((a, b) => a.groupIdentifier - b.groupIdentifier);
  }

  getCorrectiveActionItems(
    caLineItemResponse: CorrectiveActionLineItemResponse,
    inspectorsMap: Map<number, InspectorsResponse> | null,
    sitesMap: Map<number, SitesResponse> | null
  ): CAItem[] {
    if (!caLineItemResponse) return [];
   
    const items: CAItem[] = [];
    (caLineItemResponse.InspectionResults ?? []).forEach(ir => {
      (ir.CorrectiveActions ?? [])
        .forEach(ca => {
          const inspector = ca.IssuedUserId ? inspectorsMap?.get(ca.IssuedUserId) : null;
          const site = sitesMap?.get(ca.SiteId);
          items.push({
            id: ca.Id,
          issue: ca.Issue,
          statusId: ca.StatusId,
          issued: ca.Issued,
          issuedById: ca.IssuedUserId,
          issuedByName: inspector?.Name ?? `User ${ca.IssuedUserId}`,
          validationComments: ca.ValidationComments,
          validatedOn: ca.ValidatedOn,
          isValid: ca.IsValid,
          accepted: ca.Accepted,
          acceptedBy: ca.AcceptedBy,
          lastResponseDate: ca.LastResponseDate,
          lastResponseBy: ca.LastResponseBy,
          lastResponseType: ca.LastResponseType,
          lastResponseComments: ca.LastResponseComments,
          classificationId: ca.ClassificationId,
          attachmentCount: ca.AttachmentCount ?? 0,
          responseAttachmentCount: ca.ResponseAttachmentCount ?? 0,
          siteId: ca.SiteId,
          siteName: site?.Name ?? '',
          inspectionResultId: ir.Id || null,
          inspectionDate: ir.InspectedOn ?? null,
          raw: ca,
        });
      });
    });

    return items;
  }

  /** Flattens the cached raw response into one row per corrective action, for the grid view. Requires getGroupedCAData() to have run first. */
  getAllCorrectiveActionItems(): CAGridRow[] {
    if (!this.rawData) return [];

    const inspectorsMap = lookupDataService.getInspectors();
    const sitesMap = lookupDataService.getSites();
    const statuses = lookupDataService.getCorrectiveActionStatuses();
    const validationStatuses = lookupDataService.getCAValidationStatuses();
    const classifications = lookupDataService.getClassifications();

    const rows: CAGridRow[] = [];
    for (const group of this.rawData) {
      for (const lineItem of group.LineItems) {
        const items = this.getCorrectiveActionItems(lineItem, inspectorsMap, sitesMap);
        for (const item of items) {
          const validationStatusId = isValidToStatusId(item.isValid);
          rows.push({
            ...item,
            groupId: group.Id,
            groupIdentifier: (group as any).Identifier ?? group.Id,
            groupTitle: group.Title,
            lineItemId: lineItem.Id,
            lineItem: lineItem.LineItem,
            lineItemTitle: lineItem.Title,
            statusName: statuses.find(s => s.Id === item.statusId)?.Name ?? `Status ${item.statusId}`,
            validationStatusName: validationStatuses.find(s => s.Id === validationStatusId)?.Name ?? '',
            classificationName: item.classificationId != null ? (classifications?.get(item.classificationId)?.Name ?? '') : '',
          });
        }
      }
    }
    return rows;
  }

  async getCorrectiveActionItem(caId: number): Promise<CAItem | null> {
    const caResponse: CorrectiveActionLineItemResponse = await apiService.getContractCorrectiveAction(caId);
    if (!caResponse) return null;
    
    const inspectorsMap = lookupDataService.getInspectors();
    const sitesMap = lookupDataService.getSites();
    const items = this.getCorrectiveActionItems(caResponse, inspectorsMap, sitesMap);
    return items && items.length > 0 ? items[0] : null;
  }


  /** Resolves and returns corrective actions for a line item (name lookups, site mapping). */
  async getCorrectiveActionsForLineItem(groupId: number, lineItemId: number, subItems: CAId[]): Promise<CAItem[]> {
    if (!this.rawData) return [];
    const group = this.rawData.find(g => g.Id === groupId);
    const lineItem = group?.LineItems.find(li => li.Id === lineItemId);
    if (!lineItem) return [];

    const inspectorsMap = lookupDataService.getInspectors();
    const sitesMap = lookupDataService.getSites();

    const filteredCAResponse: CorrectiveActionLineItemResponse = {
      ...lineItem,
      InspectionResults: lineItem.InspectionResults?.map(ir => ({
        ...ir,
        CorrectiveActions: ir.CorrectiveActions?.filter(ca => subItems.some(idObj => idObj.id === ca.Id)) ?? null,
      })) ?? null,
    };

    return this.getCorrectiveActionItems(filteredCAResponse, inspectorsMap, sitesMap);
  }

  /** Returns all groups from line items data (async, cached). */
  async getAvailableGroups(): Promise<{ id: number; identifier: number; title: string }[]> {
    const data = await this.ensureLineItemsLoaded();
    return data
      .map(g => ({
        id: g.Id,
        identifier: (g as any).Identifier ?? g.Id,
        title: g.Title,
      }))
      .sort((a, b) => a.identifier - b.identifier);
  }

  /** Returns all line items for a group from line items data (async, cached). */
  async getAvailableLineItems(groupId: number): Promise<{ id: number; lineItem: string; title: string }[]> {
    const data = await this.ensureLineItemsLoaded();
    const group = data.find(g => g.Id === groupId);
    if (!group) return [];
    return group.LineItems.map(li => ({
      id: li.Id,
      lineItem: li.LineItem,
      title: li.Title,
    })).sort((a, b) => a.lineItem.localeCompare(b.lineItem));
  }

  /** Returns inspection results for a line item, sorted by date desc with nulls last */
  getInspectionResultsForLineItem(groupId: number, lineItemId: number): InspectionResultOption[] {
    const inspectionIdentifiersMap = inspectionService.getInspectionIdentifiersMap(groupId, lineItemId);
    if (!inspectionIdentifiersMap) return [];

    const sitesMap = lookupDataService.getSites();
    return [...inspectionIdentifiersMap.values()]
      .map(ir => ({
        id: ir.id,
        date: ir.inspectedOn ? new Date(ir.inspectedOn).toLocaleDateString() : 'No Inspection Date',
        siteName: sitesMap?.get(ir.siteId)?.Name ?? `Site ${ir.siteId}`,
        siteId: ir.siteId,
      }))
      .sort((a, b) => {
        const dateA = a.date === 'No Inspection Date' ? null : new Date(a.date);
        const dateB = b.date === 'No Inspection Date' ? null : new Date(b.date);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.getTime() - dateA.getTime();
      });
  }
}

export const caOrchestrationService = new CAOrchestrationService();
export default caOrchestrationService;
